import { LOCALSTORAGE_KEY } from "@config";
import report from "@report";

export const getLocalStorageItem = ({ key }: { key: string }) => {
  const username = localStorage.getItem(LOCALSTORAGE_KEY.USERNAME) || "";
  return localStorage.getItem(username + "|" + key);
};

export const setLocalStorageItem = ({ key, value }: { key: string; value: string }) => {
  const username = localStorage.getItem(LOCALSTORAGE_KEY.USERNAME) || "";
  try {
    return localStorage.setItem(username + "|" + key, value);
  } catch (error) {
    // 处理 QuotaExceededError - localStorage 配额超出
    if (error instanceof DOMException && error.name === "QuotaExceededError") {
      console.warn(`localStorage quota exceeded when setting key: ${key}. Attempting to clean up...`);
      // 尝试清理旧的 GlobalNotify 数据和其他账号的 key
      try {
        const allKeys = Object.keys(localStorage);
        const currentUserPrefix = username + "|";

        // 1. 清理旧的 GlobalNotify 键（保留最近的 5 个）
        const globalNotifyKeys = allKeys.filter((k) => k.includes("GlobalNotify-"));
        if (globalNotifyKeys.length > 5) {
          globalNotifyKeys.slice(0, globalNotifyKeys.length - 5).forEach((k) => {
            localStorage.removeItem(k);
          });
        }

        // 2. 清理其他账号的 key（不属于当前用户的键）
        const otherAccountKeys = allKeys.filter((k) => {
          // 找出格式为 "username|key" 的键，且不属于当前用户
          if (k.includes("|") && !k.startsWith(currentUserPrefix)) {
            return true;
          }
          return false;
        });

        // 清理其他账号的 key（限制数量，避免一次性清理太多）
        if (otherAccountKeys.length > 0) {
          // 优先清理 GlobalNotify 相关的其他账号 key
          const otherAccountGlobalNotifyKeys = otherAccountKeys.filter((k) => k.includes("GlobalNotify-"));
          otherAccountGlobalNotifyKeys.forEach((k) => {
            localStorage.removeItem(k);
          });

          // 如果清理后仍然空间不足，清理其他账号的其他 key（最多清理 100 个）
          const remainingOtherKeys = otherAccountKeys.filter((k) => !k.includes("GlobalNotify-"));
          if (remainingOtherKeys.length > 0) {
            remainingOtherKeys.slice(0, 100).forEach((k) => {
              localStorage.removeItem(k);
            });
          }
        }

        // 重试设置
        return localStorage.setItem(username + "|" + key, value);
      } catch (retryError) {
        console.error("Failed to clean up localStorage and retry:", retryError);
        throw error;
      }
    }
    throw error;
  }
};

export const removeLocalStorageItem = ({ key }: { key: string }) => {
  const username = localStorage.getItem(LOCALSTORAGE_KEY.USERNAME) || "";
  const fullKey = username + "|" + key;

  // 检查 key 是否存在
  const existed = localStorage.getItem(fullKey) !== null;

  // 移除 localStorage
  localStorage.removeItem(fullKey);

  // 如果 key 存在，触发自定义事件
  if (existed) {
    const event = new CustomEvent("localStorageRemove", {
      detail: { key, fullKey },
    });
    window.dispatchEvent(event);
  }
};

export const getLocalStorageJson = ({ key }: { key: string }) => {
  const username = localStorage.getItem(LOCALSTORAGE_KEY.USERNAME) || "";
  const value = localStorage.getItem(username + "|" + key);
  if (value) {
    try {
      return JSON.parse(value);
    } catch (err) {}
  }
  return null;
};
1;

export const setLocalStorageJson = ({ key, value }: { key: string; value: any }) => {
  const username = localStorage.getItem(LOCALSTORAGE_KEY.USERNAME) || "";
  try {
    return localStorage.setItem(username + "|" + key, JSON.stringify(value));
  } catch (error) {
    // 处理 QuotaExceededError - localStorage 配额超出
    if (error instanceof DOMException && error.name === "QuotaExceededError") {
      console.warn(`localStorage quota exceeded when setting key: ${key}. Attempting to clean up...`);
      // 增加错误日志
      report.event("error_event", {
        kalo_pos: "setLocalStorageJson",
        kalo_value: "localStorageQuotaExceeded",
        kalo_type: key,
      });
      // 尝试清理旧的 GlobalNotify 数据和其他账号的 key
      try {
        const allKeys = Object.keys(localStorage);
        const currentUserPrefix = username + "|";

        // 1. 清理旧的 GlobalNotify 键（保留最近的 5 个）
        const globalNotifyKeys = allKeys.filter((k) => k.includes("GlobalNotify-"));
        if (globalNotifyKeys.length > 5) {
          globalNotifyKeys.slice(0, globalNotifyKeys.length - 5).forEach((k) => {
            localStorage.removeItem(k);
          });
        }

        // 2. 清理其他账号的 key（不属于当前用户的键）
        const otherAccountKeys = allKeys.filter((k) => {
          // 找出格式为 "username|key" 的键，且不属于当前用户
          if (k.includes("|") && !k.startsWith(currentUserPrefix)) {
            return true;
          }
          return false;
        });

        // 清理其他账号的 key（限制数量，避免一次性清理太多）
        if (otherAccountKeys.length > 0) {
          // 优先清理 GlobalNotify 相关的其他账号 key
          const otherAccountGlobalNotifyKeys = otherAccountKeys.filter((k) => k.includes("GlobalNotify-"));
          otherAccountGlobalNotifyKeys.forEach((k) => {
            localStorage.removeItem(k);
          });

          // 如果清理后仍然空间不足，清理其他账号的其他 key（最多清理 100 个）
          const remainingOtherKeys = otherAccountKeys.filter((k) => !k.includes("GlobalNotify-"));
          if (remainingOtherKeys.length > 0) {
            remainingOtherKeys.slice(0, 100).forEach((k) => {
              localStorage.removeItem(k);
            });
          }
        }

        // 重试设置
        return localStorage.setItem(username + "|" + key, JSON.stringify(value));
      } catch (retryError) {
        console.error("Failed to clean up localStorage and retry:", retryError);
        throw error;
      }
    }
    throw error;
  }
};

export const getSessionStorageJson = ({ key }: { key: string }) => {
  const value = sessionStorage.getItem(key);
  if (value) {
    try {
      return JSON.parse(value);
    } catch (err) {}
  }
  return null;
};
1;

export const setSessionStorageJson = ({ key, value }: { key: string; value: any }) => {
  return sessionStorage.setItem(key, JSON.stringify(value));
};

export const LoginMethodStorage = (loginType: "sms" | "email" | "password" | "email_password" | "wxcode") => {
  let loginMethod = localStorage.getItem(LOCALSTORAGE_KEY.LOGIN_METHOD);
  if (loginType === "sms" || loginType === "password") {
    loginMethod = "password";
  } else if (loginType === "wxcode") {
    loginMethod = "wxcode";
  } else {
    loginMethod = "email_password";
  }
  localStorage.setItem(LOCALSTORAGE_KEY.LOGIN_METHOD, loginMethod);
};
